import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Divider,
  Grid,
  Chip,
  TextField,
  Stack,
  List,
  ListItem,
  ListItemText,
  useTheme,
  useMediaQuery,
} from '@mui/material'
import { useEffect, useMemo, useState } from 'react'
import { toast } from 'react-toastify'
import { Deposit, Withdrawal, SmsWithdrawalRequest } from '../types/transactions'

interface TransactionDetailModalProps {
  open: boolean
  onClose: () => void
  transaction: Deposit | Withdrawal | SmsWithdrawalRequest | null
  type: 'deposit' | 'withdrawal' | 'sms-request'
}

const NOTES_STORAGE_KEY = 'iris_ui_tx_notes'

const TransactionDetailModal = ({ open, onClose, transaction, type }: TransactionDetailModalProps) => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const [note, setNote] = useState('')

  useEffect(() => {
    if (!transaction) return
    if (typeof window === 'undefined') return
    const raw = localStorage.getItem(NOTES_STORAGE_KEY)
    if (!raw) {
      setNote('')
      return
    }
    const notes = JSON.parse(raw) as Record<string, string>
    setNote(notes[transaction.id] || '')
  }, [transaction])

  const saveNote = () => {
    if (!transaction || typeof window === 'undefined') return
    const raw = localStorage.getItem(NOTES_STORAGE_KEY)
    const notes = raw ? (JSON.parse(raw) as Record<string, string>) : {}
    notes[transaction.id] = note
    localStorage.setItem(NOTES_STORAGE_KEY, JSON.stringify(notes))
    toast.success('Nota a fost salvată')
  }

  const createdAt =
    transaction && 'created_at' in transaction ? transaction.created_at : transaction?.createdAt ?? new Date().toISOString()

  const statusHistory = useMemo(() => {
    if (!transaction) return []
    const statusName =
      type === 'deposit'
        ? (transaction as Deposit).status.name
        : type === 'withdrawal'
          ? (transaction as Withdrawal).status.name
          : (transaction as SmsWithdrawalRequest).status || 'PENDING'
    return [
      { label: 'Received', detail: 'Request received by system', timestamp: createdAt },
      { label: 'Processing', detail: 'Processing with provider', timestamp: new Date(new Date(createdAt).getTime() + 5 * 60000).toISOString() },
      { label: statusName, detail: 'Current status', timestamp: new Date(new Date(createdAt).getTime() + 15 * 60000).toISOString() },
    ]
  }, [transaction, type, createdAt])

  if (!transaction) return null

  const renderCommentSection = () => {
    if (type === 'deposit' || type === 'withdrawal') {
      const commentText =
        type === 'deposit' 
          ? ((transaction as any).comment ?? null)
          : ((transaction as any).comment ?? null)
      return (
        <Box sx={{ mt: 2 }}>
          <Typography variant="h6" gutterBottom>
            Comentarii & Note
          </Typography>
          <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
            {commentText?.trim() || 'Nu există comentarii pentru această tranzacție.'}
          </Typography>
        </Box>
      )
    }

    return (
      <>
        <Typography variant="h6" gutterBottom>
          Comentarii & Note
        </Typography>
        <Stack spacing={2} mt={1}>
          <TextField
            multiline
            minRows={3}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Adaugă observații despre această tranzacție"
          />
          <Button variant="contained" onClick={saveNote} disabled={!transaction}>
            Salvează nota
          </Button>
        </Stack>
      </>
    )
  }

  const renderDepositDetails = (deposit: Deposit) => (
    <Grid container spacing={isMobile ? 2 : 3}>
      {/* Basic Information */}
      <Grid item xs={12}>
        <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
          Basic Information
        </Typography>
        <Divider sx={{ mb: 2 }} />
      </Grid>
      <Grid item xs={12} sm={6}>
        <Typography variant="subtitle2" color="text.secondary">
          MSISDN
        </Typography>
        <Typography variant="body1">{deposit.msisdn}</Typography>
      </Grid>
      <Grid item xs={12} sm={6}>
        <Typography variant="subtitle2" color="text.secondary">
          Paybill
        </Typography>
        <Typography variant="body1">{deposit.paybill}</Typography>
      </Grid>
      <Grid item xs={12} sm={6}>
        <Typography variant="subtitle2" color="text.secondary">
          Status
        </Typography>
        <Chip
          label={deposit.status.name}
          color={
            deposit.status.name === 'COMPLETED'
              ? 'success'
              : deposit.status.name === 'FAILED'
                ? 'error'
                : 'warning'
          }
          size="small"
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <Typography variant="subtitle2" color="text.secondary">
          Channel
        </Typography>
        <Typography variant="body1">{deposit.channel.name}</Typography>
      </Grid>

      {/* Financial Details */}
      <Grid item xs={12}>
        <Typography variant={isMobile ? "subtitle1" : "h6"} gutterBottom sx={{ fontWeight: 600, mt: 1 }}>
          Financial Details
        </Typography>
        <Divider sx={{ mb: 2 }} />
      </Grid>
      <Grid item xs={12} sm={4}>
        <Typography variant="subtitle2" color="text.secondary">
          Amount
        </Typography>
        <Typography variant="body1" sx={{ fontWeight: 500 }}>
          KES {((deposit as any).amount ?? 0).toFixed(2)}
        </Typography>
      </Grid>
      <Grid item xs={12} sm={4}>
        <Typography variant="subtitle2" color="text.secondary">
          Amount Sent
        </Typography>
        <Typography variant="body1">
          KES {((deposit as any).amountSent ?? (deposit as any).amount_sent ?? 0).toFixed(2)}
        </Typography>
      </Grid>
      <Grid item xs={12} sm={4}>
        <Typography variant="subtitle2" color="text.secondary">
          Tax Applied
        </Typography>
        <Typography variant="body1">
          KES {((deposit as any).appliedTax ?? (deposit as any).applied_tax ?? 0).toFixed(2)}
        </Typography>
      </Grid>

      {/* Transaction IDs */}
      <Grid item xs={12}>
        <Typography variant={isMobile ? "subtitle1" : "h6"} gutterBottom sx={{ fontWeight: 600, mt: 1 }}>
          Transaction IDs
        </Typography>
        <Divider sx={{ mb: 2 }} />
      </Grid>
      <Grid item xs={12} sm={6}>
        <Typography variant="subtitle2" color="text.secondary">
          M-Pesa Receipt Number
        </Typography>
        <Typography variant="body1" sx={{ wordBreak: 'break-all' }}>
          {(deposit as any).mpesaReceiptNumber ?? (deposit as any).mpesa_receipt_number ?? 'N/A'}
        </Typography>
      </Grid>
      <Grid item xs={12} sm={6}>
        <Typography variant="subtitle2" color="text.secondary">
          External Transaction ID
        </Typography>
        <Typography variant="body1" sx={{ wordBreak: 'break-all' }}>
          {(deposit as any).externalTransactionId ?? (deposit as any).external_transaction_id ?? 'N/A'}
        </Typography>
      </Grid>
      <Grid item xs={12} sm={6}>
        <Typography variant="subtitle2" color="text.secondary">
          Merchant Request ID
        </Typography>
        <Typography variant="body2" sx={{ wordBreak: 'break-all', fontFamily: 'monospace' }}>
          {(deposit as any).merchantRequestId ?? (deposit as any).merchant_request_id ?? 'N/A'}
        </Typography>
      </Grid>
      <Grid item xs={12} sm={6}>
        <Typography variant="subtitle2" color="text.secondary">
          Checkout Request ID
        </Typography>
        <Typography variant="body2" sx={{ wordBreak: 'break-all', fontFamily: 'monospace' }}>
          {(deposit as any).checkoutRequestId ?? (deposit as any).checkout_request_id ?? 'N/A'}
        </Typography>
      </Grid>
      <Grid item xs={12} sm={6}>
        <Typography variant="subtitle2" color="text.secondary">
          External User ID
        </Typography>
        <Typography variant="body1">{((deposit as any).externalUserId ?? (deposit as any).external_user_id) || 'N/A'}</Typography>
      </Grid>

      {/* BtoBet Integration */}
      {((deposit as any).btobetResponse ?? (deposit as any).btobet_response) && (
        <>
          <Grid item xs={12}>
            <Typography variant={isMobile ? "subtitle1" : "h6"} gutterBottom sx={{ fontWeight: 600, mt: 1 }}>
              BtoBet Integration
            </Typography>
            <Divider sx={{ mb: 2 }} />
          </Grid>
          <Grid item xs={12}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Response
            </Typography>
            <Box
              sx={{
                p: 2,
                bgcolor: 'grey.50',
                border: '1px solid',
                borderColor: 'grey.300',
                borderRadius: 1,
                maxHeight: '300px',
                overflow: 'auto',
              }}
            >
              <Typography
                variant="body2"
                component="pre"
                sx={{
                  fontFamily: 'monospace',
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                  margin: 0,
                  fontSize: '0.875rem',
                }}
              >
                {(() => {
                  const btobetResponse = (deposit as any).btobetResponse ?? (deposit as any).btobet_response
                  if (!btobetResponse) return 'N/A'
                  try {
                    const parsed = JSON.parse(btobetResponse)
                    return JSON.stringify(parsed, null, 2)
                  } catch {
                    return btobetResponse
                  }
                })()}
              </Typography>
            </Box>
          </Grid>
        </>
      )}

      {/* M-Pesa Integration */}
      {((deposit as any).mpesaResponse ?? (deposit as any).mpesa_response) && (
        <>
          <Grid item xs={12}>
            <Typography variant={isMobile ? "subtitle1" : "h6"} gutterBottom sx={{ fontWeight: 600, mt: 1 }}>
              M-Pesa Integration
            </Typography>
            <Divider sx={{ mb: 2 }} />
          </Grid>
          {(() => {
            const mpesaResponse = (deposit as any).mpesaResponse ?? (deposit as any).mpesa_response
            if (!mpesaResponse) return null
            try {
              const parsed = JSON.parse(mpesaResponse)
              const callback = parsed?.stk_callback || parsed
              const metadata = callback?.callback_metadata?.item || []
              
              // Extract key information
              const receiptNumber = metadata.find((item: any) => item?.name === 'MpesaReceiptNumber')?.value
              const amount = metadata.find((item: any) => item?.name === 'Amount')?.value
              const phoneNumber = metadata.find((item: any) => item?.name === 'PhoneNumber')?.value
              const transactionDate = metadata.find((item: any) => item?.name === 'TransactionDate')?.value
              const balance = metadata.find((item: any) => item?.name === 'Balance')?.value
              
              return (
                <>
                  {/* Key Information */}
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Merchant Request ID
                    </Typography>
                    <Typography variant="body2" sx={{ wordBreak: 'break-all', fontFamily: 'monospace' }}>
                      {callback?.merchant_request_id || 'N/A'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Checkout Request ID
                    </Typography>
                    <Typography variant="body2" sx={{ wordBreak: 'break-all', fontFamily: 'monospace' }}>
                      {callback?.checkout_request_id || 'N/A'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Result Code
                    </Typography>
                    <Chip
                      label={callback?.result_code || 'N/A'}
                      color={callback?.result_code === 0 ? 'success' : 'error'}
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Result Description
                    </Typography>
                    <Typography variant="body2">
                      {callback?.result_desc || 'N/A'}
                    </Typography>
                  </Grid>
                  
                  {/* Transaction Details */}
                  {receiptNumber && (
                    <Grid item xs={12} sm={6}>
                      <Typography variant="subtitle2" color="text.secondary">
                        M-Pesa Receipt Number
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 500, fontFamily: 'monospace' }}>
                        {receiptNumber}
                      </Typography>
                    </Grid>
                  )}
                  {amount && (
                    <Grid item xs={12} sm={6}>
                      <Typography variant="subtitle2" color="text.secondary">
                        Amount
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        KES {parseFloat(amount).toFixed(2)}
                      </Typography>
                    </Grid>
                  )}
                  {phoneNumber && (
                    <Grid item xs={12} sm={6}>
                      <Typography variant="subtitle2" color="text.secondary">
                        Phone Number
                      </Typography>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                        {phoneNumber}
                      </Typography>
                    </Grid>
                  )}
                  {transactionDate && (
                    <Grid item xs={12} sm={6}>
                      <Typography variant="subtitle2" color="text.secondary">
                        Transaction Date
                      </Typography>
                      <Typography variant="body2">
                        {(() => {
                          try {
                            const dateStr = transactionDate.toString()
                            const year = dateStr.substring(0, 4)
                            const month = dateStr.substring(4, 6)
                            const day = dateStr.substring(6, 8)
                            const hour = dateStr.substring(8, 10)
                            const minute = dateStr.substring(10, 12)
                            const second = dateStr.substring(12, 14)
                            return new Date(`${year}-${month}-${day}T${hour}:${minute}:${second}`).toLocaleString()
                          } catch {
                            return transactionDate
                          }
                        })()}
                      </Typography>
                    </Grid>
                  )}
                  {balance !== null && balance !== undefined && (
                    <Grid item xs={12} sm={6}>
                      <Typography variant="subtitle2" color="text.secondary">
                        Balance
                      </Typography>
                      <Typography variant="body2">
                        {balance ? `KES ${parseFloat(balance).toFixed(2)}` : 'N/A'}
                      </Typography>
                    </Grid>
                  )}
                  
                  {/* Full JSON Response (Collapsible) */}
                  <Grid item xs={12}>
                    <Box
                      sx={{
                        mt: 2,
                        p: 2,
                        bgcolor: 'grey.50',
                        border: '1px solid',
                        borderColor: 'grey.300',
                        borderRadius: 1,
                      }}
                    >
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                        Full JSON Response
                      </Typography>
                      <Box
                        sx={{
                          p: 1.5,
                          bgcolor: 'white',
                          borderRadius: 1,
                          maxHeight: '300px',
                          overflow: 'auto',
                        }}
                      >
                        <Typography
                          variant="body2"
                          component="pre"
                          sx={{
                            fontFamily: 'monospace',
                            whiteSpace: 'pre-wrap',
                            wordBreak: 'break-word',
                            margin: 0,
                            fontSize: '0.75rem',
                          }}
                        >
                          {JSON.stringify(parsed, null, 2)}
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                </>
              )
            } catch {
              // Fallback to raw display if JSON parsing fails
              return (
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Response
                  </Typography>
                  <Box
                    sx={{
                      p: 2,
                      bgcolor: 'grey.50',
                      border: '1px solid',
                      borderColor: 'grey.300',
                      borderRadius: 1,
                      maxHeight: '300px',
                      overflow: 'auto',
                    }}
                  >
                    <Typography
                      variant="body2"
                      component="pre"
                      sx={{
                        fontFamily: 'monospace',
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word',
                        margin: 0,
                        fontSize: '0.875rem',
                      }}
                    >
                      {deposit.mpesa_response}
                    </Typography>
                  </Box>
                </Grid>
              )
            }
          })()}
        </>
      )}

      {/* Timestamps */}
      <Grid item xs={12}>
        <Typography variant={isMobile ? "subtitle1" : "h6"} gutterBottom sx={{ fontWeight: 600, mt: 1 }}>
          Timestamps
        </Typography>
        <Divider sx={{ mb: 2 }} />
      </Grid>
      <Grid item xs={12} sm={6}>
        <Typography variant="subtitle2" color="text.secondary">
          Created At
        </Typography>
        <Typography variant="body1">
          {new Date(deposit.created_at).toLocaleString()}
        </Typography>
      </Grid>
      <Grid item xs={12} sm={6}>
        <Typography variant="subtitle2" color="text.secondary">
          Updated At
        </Typography>
        <Typography variant="body1">
          {new Date(deposit.updated_at).toLocaleString()}
        </Typography>
      </Grid>
    </Grid>
  )

  const renderWithdrawalDetails = (withdrawal: Withdrawal) => (
    <Grid container spacing={isMobile ? 2 : 3}>
      {/* Basic Information */}
      <Grid item xs={12}>
        <Typography variant={isMobile ? "subtitle1" : "h6"} gutterBottom sx={{ fontWeight: 600 }}>
          Basic Information
        </Typography>
        <Divider sx={{ mb: 2 }} />
      </Grid>
      <Grid item xs={12} sm={6}>
        <Typography variant="subtitle2" color="text.secondary">
          MSISDN
        </Typography>
        <Typography variant="body1">{withdrawal.msisdn}</Typography>
      </Grid>
      <Grid item xs={12} sm={6}>
        <Typography variant="subtitle2" color="text.secondary">
          Paybill
        </Typography>
        <Typography variant="body1">{withdrawal.paybill}</Typography>
      </Grid>
      <Grid item xs={12} sm={6}>
        <Typography variant="subtitle2" color="text.secondary">
          Status
        </Typography>
        <Chip
          label={withdrawal.status.name}
          color={
            withdrawal.status.name === 'COMPLETED'
              ? 'success'
              : withdrawal.status.name === 'FAILED'
                ? 'error'
                : 'warning'
          }
          size="small"
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <Typography variant="subtitle2" color="text.secondary">
          Channel
        </Typography>
        <Typography variant="body1">{withdrawal.channel.name}</Typography>
      </Grid>

      {/* Financial Details */}
      <Grid item xs={12}>
        <Typography variant={isMobile ? "subtitle1" : "h6"} gutterBottom sx={{ fontWeight: 600, mt: 1 }}>
          Financial Details
        </Typography>
        <Divider sx={{ mb: 2 }} />
      </Grid>
      <Grid item xs={12} sm={4}>
        <Typography variant="subtitle2" color="text.secondary">
          Amount
        </Typography>
        <Typography variant="body1" sx={{ fontWeight: 500 }}>
          KES {((withdrawal as any).amount ?? 0).toFixed(2)}
        </Typography>
      </Grid>
      <Grid item xs={12} sm={4}>
        <Typography variant="subtitle2" color="text.secondary">
          Amount Sent
        </Typography>
        <Typography variant="body1">
          KES {((withdrawal as any).amountSent ?? (withdrawal as any).amount_sent ?? 0).toFixed(2)}
        </Typography>
      </Grid>
      <Grid item xs={12} sm={4}>
        <Typography variant="subtitle2" color="text.secondary">
          Tax Applied
        </Typography>
        <Typography variant="body1">
          KES {((withdrawal as any).appliedTax ?? (withdrawal as any).applied_tax ?? 0).toFixed(2)}
        </Typography>
      </Grid>

      {/* Transaction IDs */}
      <Grid item xs={12}>
        <Typography variant={isMobile ? "subtitle1" : "h6"} gutterBottom sx={{ fontWeight: 600, mt: 1 }}>
          Transaction IDs
        </Typography>
        <Divider sx={{ mb: 2 }} />
      </Grid>
      <Grid item xs={12} sm={6}>
        <Typography variant="subtitle2" color="text.secondary">
          M-Pesa Receipt Number
        </Typography>
        <Typography variant="body1" sx={{ wordBreak: 'break-all' }}>
          {((withdrawal as any).mpesaReceiptNumber ?? (withdrawal as any).mpesa_receipt_number) || 'N/A'}
        </Typography>
      </Grid>
      <Grid item xs={12} sm={6}>
        <Typography variant="subtitle2" color="text.secondary">
          External Transaction ID
        </Typography>
        <Typography variant="body1" sx={{ wordBreak: 'break-all' }}>
          {((withdrawal as any).externalTransactionId ?? (withdrawal as any).external_transaction_id) || 'N/A'}
        </Typography>
      </Grid>
      <Grid item xs={12} sm={6}>
        <Typography variant="subtitle2" color="text.secondary">
          Conversation ID
        </Typography>
        <Typography variant="body2" sx={{ wordBreak: 'break-all', fontFamily: 'monospace' }}>
          {((withdrawal as any).conversationId ?? (withdrawal as any).conversation_id) || 'N/A'}
        </Typography>
      </Grid>
      <Grid item xs={12} sm={6}>
        <Typography variant="subtitle2" color="text.secondary">
          Originator Conversation ID
        </Typography>
        <Typography variant="body2" sx={{ wordBreak: 'break-all', fontFamily: 'monospace' }}>
          {((withdrawal as any).originatorConversationId ?? (withdrawal as any).originator_conversation_id) || 'N/A'}
        </Typography>
      </Grid>
      <Grid item xs={12} sm={6}>
        <Typography variant="subtitle2" color="text.secondary">
          External User ID
        </Typography>
        <Typography variant="body1">{((withdrawal as any).externalUserId ?? (withdrawal as any).external_user_id) || 'N/A'}</Typography>
      </Grid>
      {((withdrawal as any).smsWithdrawalRequestId ?? (withdrawal as any).sms_withdrawal_request_id) && (
        <Grid item xs={12} sm={6}>
          <Typography variant="subtitle2" color="text.secondary">
            SMS Withdrawal Request ID
          </Typography>
          <Typography variant="body1" sx={{ wordBreak: 'break-all' }}>
            {(withdrawal as any).smsWithdrawalRequestId ?? (withdrawal as any).sms_withdrawal_request_id}
          </Typography>
        </Grid>
      )}

      {/* BtoBet Integration */}
      {((withdrawal as any).btobetResponse ?? (withdrawal as any).btobet_response) && (
        <>
          <Grid item xs={12}>
            <Typography variant={isMobile ? "subtitle1" : "h6"} gutterBottom sx={{ fontWeight: 600, mt: 1 }}>
              BtoBet Integration
            </Typography>
            <Divider sx={{ mb: 2 }} />
          </Grid>
          <Grid item xs={12}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Response
            </Typography>
            <Box
              sx={{
                p: 2,
                bgcolor: 'grey.50',
                border: '1px solid',
                borderColor: 'grey.300',
                borderRadius: 1,
                maxHeight: '300px',
                overflow: 'auto',
              }}
            >
              <Typography
                variant="body2"
                component="pre"
                sx={{
                  fontFamily: 'monospace',
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                  margin: 0,
                  fontSize: '0.875rem',
                }}
              >
                {(() => {
                  const btobetResponse = (withdrawal as any).btobetResponse ?? (withdrawal as any).btobet_response
                  if (!btobetResponse) return 'N/A'
                  try {
                    const parsed = JSON.parse(btobetResponse)
                    return JSON.stringify(parsed, null, 2)
                  } catch {
                    return btobetResponse
                  }
                })()}
              </Typography>
            </Box>
          </Grid>
        </>
      )}

      {/* M-Pesa Integration */}
      {((withdrawal as any).mpesaResponse ?? (withdrawal as any).mpesa_response) && (
        <>
          <Grid item xs={12}>
            <Typography variant={isMobile ? "subtitle1" : "h6"} gutterBottom sx={{ fontWeight: 600, mt: 1 }}>
              M-Pesa Integration
            </Typography>
            <Divider sx={{ mb: 2 }} />
          </Grid>
          {(() => {
            const mpesaResponse = (withdrawal as any).mpesaResponse ?? (withdrawal as any).mpesa_response
            if (!mpesaResponse) return null
            try {
              const parsed = JSON.parse(mpesaResponse)
              const callback = parsed?.stk_callback || parsed
              const metadata = callback?.callback_metadata?.item || []
              
              // Extract key information
              const receiptNumber = metadata.find((item: any) => item?.name === 'MpesaReceiptNumber')?.value
              const amount = metadata.find((item: any) => item?.name === 'Amount')?.value
              const phoneNumber = metadata.find((item: any) => item?.name === 'PhoneNumber')?.value
              const transactionDate = metadata.find((item: any) => item?.name === 'TransactionDate')?.value
              const balance = metadata.find((item: any) => item?.name === 'Balance')?.value
              
              return (
                <>
                  {/* Key Information */}
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Merchant Request ID
                    </Typography>
                    <Typography variant="body2" sx={{ wordBreak: 'break-all', fontFamily: 'monospace' }}>
                      {callback?.merchant_request_id || 'N/A'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Checkout Request ID
                    </Typography>
                    <Typography variant="body2" sx={{ wordBreak: 'break-all', fontFamily: 'monospace' }}>
                      {callback?.checkout_request_id || 'N/A'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Result Code
                    </Typography>
                    <Chip
                      label={callback?.result_code || 'N/A'}
                      color={callback?.result_code === 0 ? 'success' : 'error'}
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Result Description
                    </Typography>
                    <Typography variant="body2">
                      {callback?.result_desc || 'N/A'}
                    </Typography>
                  </Grid>
                  
                  {/* Transaction Details */}
                  {receiptNumber && (
                    <Grid item xs={12} sm={6}>
                      <Typography variant="subtitle2" color="text.secondary">
                        M-Pesa Receipt Number
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 500, fontFamily: 'monospace' }}>
                        {receiptNumber}
                      </Typography>
                    </Grid>
                  )}
                  {amount && (
                    <Grid item xs={12} sm={6}>
                      <Typography variant="subtitle2" color="text.secondary">
                        Amount
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        KES {parseFloat(amount).toFixed(2)}
                      </Typography>
                    </Grid>
                  )}
                  {phoneNumber && (
                    <Grid item xs={12} sm={6}>
                      <Typography variant="subtitle2" color="text.secondary">
                        Phone Number
                      </Typography>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                        {phoneNumber}
                      </Typography>
                    </Grid>
                  )}
                  {transactionDate && (
                    <Grid item xs={12} sm={6}>
                      <Typography variant="subtitle2" color="text.secondary">
                        Transaction Date
                      </Typography>
                      <Typography variant="body2">
                        {(() => {
                          try {
                            const dateStr = transactionDate.toString()
                            const year = dateStr.substring(0, 4)
                            const month = dateStr.substring(4, 6)
                            const day = dateStr.substring(6, 8)
                            const hour = dateStr.substring(8, 10)
                            const minute = dateStr.substring(10, 12)
                            const second = dateStr.substring(12, 14)
                            return new Date(`${year}-${month}-${day}T${hour}:${minute}:${second}`).toLocaleString()
                          } catch {
                            return transactionDate
                          }
                        })()}
                      </Typography>
                    </Grid>
                  )}
                  {balance !== null && balance !== undefined && (
                    <Grid item xs={12} sm={6}>
                      <Typography variant="subtitle2" color="text.secondary">
                        Balance
                      </Typography>
                      <Typography variant="body2">
                        {balance ? `KES ${parseFloat(balance).toFixed(2)}` : 'N/A'}
                      </Typography>
                    </Grid>
                  )}
                  
                  {/* Full JSON Response (Collapsible) */}
                  <Grid item xs={12}>
                    <Box
                      sx={{
                        mt: 2,
                        p: 2,
                        bgcolor: 'grey.50',
                        border: '1px solid',
                        borderColor: 'grey.300',
                        borderRadius: 1,
                      }}
                    >
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                        Full JSON Response
                      </Typography>
                      <Box
                        sx={{
                          p: 1.5,
                          bgcolor: 'white',
                          borderRadius: 1,
                          maxHeight: '300px',
                          overflow: 'auto',
                        }}
                      >
                        <Typography
                          variant="body2"
                          component="pre"
                          sx={{
                            fontFamily: 'monospace',
                            whiteSpace: 'pre-wrap',
                            wordBreak: 'break-word',
                            margin: 0,
                            fontSize: '0.75rem',
                          }}
                        >
                          {JSON.stringify(parsed, null, 2)}
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                </>
              )
            } catch {
              // Fallback to raw display if JSON parsing fails
              return (
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Response
                  </Typography>
                  <Box
                    sx={{
                      p: 2,
                      bgcolor: 'grey.50',
                      border: '1px solid',
                      borderColor: 'grey.300',
                      borderRadius: 1,
                      maxHeight: '300px',
                      overflow: 'auto',
                    }}
                  >
                    <Typography
                      variant="body2"
                      component="pre"
                      sx={{
                        fontFamily: 'monospace',
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word',
                        margin: 0,
                        fontSize: '0.875rem',
                      }}
                    >
                      {mpesaResponse}
                    </Typography>
                  </Box>
                </Grid>
              )
            }
          })()}
        </>
      )}

      {/* Comment */}
      {((withdrawal as any).comment) && (
        <>
          <Grid item xs={12}>
            <Typography variant={isMobile ? "subtitle1" : "h6"} gutterBottom sx={{ fontWeight: 600, mt: 1 }}>
              Additional Information
            </Typography>
            <Divider sx={{ mb: 2 }} />
          </Grid>
          <Grid item xs={12}>
            <Typography variant="subtitle2" color="text.secondary">
              Comment
            </Typography>
            <Typography variant="body1">{(withdrawal as any).comment || 'N/A'}</Typography>
          </Grid>
        </>
      )}

      {/* Timestamps */}
      <Grid item xs={12}>
        <Typography variant={isMobile ? "subtitle1" : "h6"} gutterBottom sx={{ fontWeight: 600, mt: 1 }}>
          Timestamps
        </Typography>
        <Divider sx={{ mb: 2 }} />
      </Grid>
      <Grid item xs={12} sm={6}>
        <Typography variant="subtitle2" color="text.secondary">
          Created At
        </Typography>
        <Typography variant="body1">
          {new Date(withdrawal.created_at).toLocaleString()}
        </Typography>
      </Grid>
      <Grid item xs={12} sm={6}>
        <Typography variant="subtitle2" color="text.secondary">
          Updated At
        </Typography>
        <Typography variant="body1">
          {new Date(withdrawal.updated_at).toLocaleString()}
        </Typography>
      </Grid>
    </Grid>
  )

  const renderSmsRequestDetails = (smsRequest: SmsWithdrawalRequest) => (
    <Grid container spacing={2}>
      <Grid item xs={12} sm={6}>
        <Typography variant="subtitle2" color="text.secondary">
          MSISDN
        </Typography>
        <Typography variant="body1">{smsRequest.msisdn}</Typography>
      </Grid>
      <Grid item xs={12} sm={6}>
        <Typography variant="subtitle2" color="text.secondary">
          Amount
        </Typography>
        <Typography variant="body1">KES {smsRequest.amount.toFixed(2)}</Typography>
      </Grid>
      <Grid item xs={12} sm={6}>
        <Typography variant="subtitle2" color="text.secondary">
          Link ID
        </Typography>
        <Typography variant="body1">{smsRequest.linkId}</Typography>
      </Grid>
      <Grid item xs={12} sm={6}>
        <Typography variant="subtitle2" color="text.secondary">
          External Transaction ID
        </Typography>
        <Typography variant="body1">{smsRequest.externalTransactionId}</Typography>
      </Grid>
      <Grid item xs={12} sm={6}>
        <Typography variant="subtitle2" color="text.secondary">
          External User ID
        </Typography>
        <Typography variant="body1">{smsRequest.externalUserId || 'N/A'}</Typography>
      </Grid>
      <Grid item xs={12} sm={6}>
        <Typography variant="subtitle2" color="text.secondary">
          Created At
        </Typography>
        <Typography variant="body1">
          {new Date(smsRequest.createdAt).toLocaleString()}
        </Typography>
      </Grid>
      <Grid item xs={12} sm={6}>
        <Typography variant="subtitle2" color="text.secondary">
          Updated At
        </Typography>
        <Typography variant="body1">
          {smsRequest.updatedAt ? new Date(smsRequest.updatedAt).toLocaleString() : 'N/A'}
        </Typography>
      </Grid>
    </Grid>
  )

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="md" 
      fullWidth
      fullScreen={isMobile}
      sx={{
        '& .MuiDialog-paper': {
          m: isMobile ? 0 : 2,
          maxHeight: isMobile ? '100%' : '90vh',
        },
      }}
    >
      <DialogTitle>
        {type === 'deposit' && 'Deposit Details'}
        {type === 'withdrawal' && 'Withdrawal Details'}
        {type === 'sms-request' && 'SMS Withdrawal Request Details'}
      </DialogTitle>
      <Divider />
      <DialogContent sx={{ px: { xs: 1.5, sm: 3 }, py: { xs: 2, sm: 3 } }}>
        <Box sx={{ mt: { xs: 1, sm: 2 } }}>
          {type === 'deposit' && renderDepositDetails(transaction as Deposit)}
          {type === 'withdrawal' && renderWithdrawalDetails(transaction as Withdrawal)}
          {type === 'sms-request' && renderSmsRequestDetails(transaction as SmsWithdrawalRequest)}
        </Box>
        <Divider sx={{ my: 3 }} />
        <Typography variant="h6" gutterBottom>
          Status History
        </Typography>
        <List dense>
          {statusHistory.map((item, idx) => (
            <ListItem key={`${item.label}-${idx}`}>
              <ListItemText
                primary={`${item.label} — ${new Date(item.timestamp).toLocaleString()}`}
                secondary={item.detail}
              />
            </ListItem>
          ))}
        </List>
        <Divider sx={{ my: 3 }} />
        {renderCommentSection()}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  )
}

export default TransactionDetailModal

